import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { useAuthStore } from '../../auth/authStore';
import { PageHeader } from '../../components/PageHeader';

interface DeliveryZone {
  deliveryZoneId: string;
  branchId: string | null;
  nameAr: string;
  nameEn: string | null;
  fee: number;
  isActive: boolean;
}

export function DeliveryZonesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tenantSlug = useAuthStore((s) => s.tenantSlug);
  const [editing, setEditing] = useState<DeliveryZone | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['delivery-zones'] as const,
    queryFn: async () => (await apiClient.get<DeliveryZone[]>('/api/online/delivery-zones')).data,
  });

  const storefrontUrl = tenantSlug ? `https://${tenantSlug}.wheels.delivery` : null;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.operations')}
        title={t('online.title')}
        subtitle={t('online.subtitle')}
      />

      {storefrontUrl && (
        <Paper p="md">
          <Group justify="space-between" wrap="wrap">
            <div>
              <Text size="sm" fw={600}>{t('online.storefrontLink')}</Text>
              <Anchor href={storefrontUrl} target="_blank" dir="ltr" size="sm">
                {storefrontUrl}
              </Anchor>
            </div>
            <Badge variant="light" color="green">{t('online.live')}</Badge>
          </Group>
        </Paper>
      )}

      <Group justify="space-between">
        <Text fw={600} size="lg">{t('online.zones')}</Text>
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setCreateOpen(true)}>
          {t('online.addZone')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? (
          <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
        ) : (data ?? []).length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">{t('online.noZones')}</Text>
        ) : (
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('online.zoneName')}</Table.Th>
                <Table.Th>{t('online.fee')}</Table.Th>
                <Table.Th>{t('online.status')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((zone) => (
                <Table.Tr key={zone.deliveryZoneId}>
                  <Table.Td>{zone.nameAr}</Table.Td>
                  <Table.Td className="font-numeric">{zone.fee.toFixed(2)} ₪</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={zone.isActive ? 'green' : 'gray'}>
                      {zone.isActive ? t('online.active') : t('online.inactive')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group justify="flex-end">
                      <Button size="xs" variant="default" onClick={() => setEditing(zone)}>
                        {t('common.edit')}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <ZoneModal
        opened={createOpen || editing !== null}
        zone={editing}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['delivery-zones'] })}
      />
    </Stack>
  );
}

function ZoneModal({
  opened, zone, onClose, onSaved,
}: {
  opened: boolean;
  zone: DeliveryZone | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [nameAr, setNameAr] = useState('');
  const [fee, setFee] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const key = zone?.deliveryZoneId ?? 'new';
  if (opened && key !== loadedFor) {
    setLoadedFor(key);
    setNameAr(zone?.nameAr ?? '');
    setFee(zone?.fee ?? 0);
    setIsActive(zone?.isActive ?? true);
  }
  if (!opened && loadedFor !== null) setLoadedFor(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = { branchId: null, nameAr: nameAr.trim(), nameEn: null, fee, isActive };
      return zone
        ? apiClient.put(`/api/online/delivery-zones/${zone.deliveryZoneId}`, body)
        : apiClient.post('/api/online/delivery-zones', body);
    },
    onSuccess: () => { onSaved(); onClose(); },
    onError: () => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={zone ? t('common.edit') : t('online.addZone')}>
      <Stack gap="sm">
        <TextInput label={t('online.zoneName')} value={nameAr}
          onChange={(e) => setNameAr(e.currentTarget.value)} withAsterisk />
        <NumberInput label={t('online.fee')} value={fee} min={0} decimalScale={2}
          onChange={(v) => setFee(typeof v === 'number' ? v : 0)} />
        <Switch label={t('online.active')} checked={isActive}
          onChange={(e) => setIsActive(e.currentTarget.checked)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!nameAr.trim()}
            onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
